/** Tags drive synergies: traits, keystones and statuses react to tags on skills and hits. */
export type Tag =
  | 'fire'
  | 'frost'
  | 'lightning'
  | 'blight'
  | 'holy'
  | 'void'
  | 'slash'
  | 'pierce'
  | 'blunt'
  | 'summon'
  | 'aura'
  | 'shield'
  | 'movement'
  | 'crit'
  | 'bleed'
  | 'heal'
  | 'curse'
  | 'projectile'
  | 'area';

export const ELEMENT_TAGS: Tag[] = ['fire', 'frost', 'lightning', 'blight', 'holy', 'void'];
export const PHYSICAL_TAGS: Tag[] = ['slash', 'pierce', 'blunt', 'bleed'];

export type StatusId =
  | 'burn'
  | 'chill'
  | 'shock'
  | 'blight'
  | 'bleed'
  | 'vulnerable'
  | 'weak'
  | 'haste'
  | 'might'
  | 'fortify'
  | 'regen'
  | 'shield'
  | 'taunt'
  | 'stun'
  | 'invuln'
  | 'tiger'
  | 'bear'
  | 'attune_fire'
  | 'attune_frost'
  | 'attune_storm'
  | 'inspire';

export interface StatusDef {
  id: StatusId;
  kind: 'buff' | 'debuff';
  maxStacks: number;
  /** Refreshing behaviour: 'refresh' resets duration, 'extend' adds it. */
  stack: 'refresh' | 'extend';
  /** Damage per second per stack as a fraction of the applier's base damage (stored as power). */
  dps?: number;
  speedMul?: number;
  dmgTakenMul?: number;
  dmgDealtMul?: number;
  armorAdd?: number;
  regenPct?: number;
  tag?: Tag;
}

export const STATUS: Record<StatusId, StatusDef> = {
  burn: { id: 'burn', kind: 'debuff', maxStacks: 5, stack: 'refresh', dps: 0.35, tag: 'fire' },
  chill: { id: 'chill', kind: 'debuff', maxStacks: 5, stack: 'refresh', speedMul: 0.88, tag: 'frost' },
  shock: { id: 'shock', kind: 'debuff', maxStacks: 3, stack: 'refresh', tag: 'lightning' },
  blight: { id: 'blight', kind: 'debuff', maxStacks: 10, stack: 'extend', dps: 0.18, tag: 'blight' },
  bleed: { id: 'bleed', kind: 'debuff', maxStacks: 5, stack: 'extend', dps: 0.25, tag: 'bleed' },
  vulnerable: { id: 'vulnerable', kind: 'debuff', maxStacks: 1, stack: 'refresh', dmgTakenMul: 1.3, tag: 'curse' },
  weak: { id: 'weak', kind: 'debuff', maxStacks: 1, stack: 'refresh', dmgDealtMul: 0.7, tag: 'curse' },
  haste: { id: 'haste', kind: 'buff', maxStacks: 1, stack: 'refresh', speedMul: 1.3 },
  might: { id: 'might', kind: 'buff', maxStacks: 1, stack: 'refresh', dmgDealtMul: 1.3 },
  fortify: { id: 'fortify', kind: 'buff', maxStacks: 1, stack: 'refresh', armorAdd: 25 },
  regen: { id: 'regen', kind: 'buff', maxStacks: 3, stack: 'refresh', regenPct: 0.02 },
  shield: { id: 'shield', kind: 'buff', maxStacks: 1, stack: 'refresh' },
  taunt: { id: 'taunt', kind: 'debuff', maxStacks: 1, stack: 'refresh' },
  stun: { id: 'stun', kind: 'debuff', maxStacks: 1, stack: 'refresh', speedMul: 0 },
  invuln: { id: 'invuln', kind: 'buff', maxStacks: 1, stack: 'refresh' },
  // hero forms and stances
  tiger: { id: 'tiger', kind: 'buff', maxStacks: 1, stack: 'refresh', speedMul: 1.22, dmgDealtMul: 1.25 },
  bear: { id: 'bear', kind: 'buff', maxStacks: 1, stack: 'refresh', armorAdd: 40, dmgTakenMul: 0.8, regenPct: 0.015 },
  attune_fire: { id: 'attune_fire', kind: 'buff', maxStacks: 1, stack: 'refresh', tag: 'fire' },
  attune_frost: { id: 'attune_frost', kind: 'buff', maxStacks: 1, stack: 'refresh', tag: 'frost' },
  attune_storm: { id: 'attune_storm', kind: 'buff', maxStacks: 1, stack: 'refresh', tag: 'lightning' },
  inspire: { id: 'inspire', kind: 'buff', maxStacks: 1, stack: 'refresh', dmgDealtMul: 1.15, speedMul: 1.1 },
};
